import { useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import { FiFileText, FiAlertTriangle, FiExternalLink, FiDownload } from "react-icons/fi"

import "./index.scss"
import "components/Lims/LimsCard.scss"
import useT from "utils/hooks/useT"
import { limsForskrifter } from "state_management"

// ForskriftViewer — read-only surface of the EFFECTIVE forskrift version on the analysis on the
// plan (PRD-003 Area 2 / SPEC-008). PDFs preview inline (iframe); Word is open/download-only
// (accepted default). The effective version is resolved by the caller via
// limsForskrifter.selectEffectiveForDefinition.
//
// SPEC-021 (B6): the file bytes live in the real S3 bucket under the server-minted team-scoped
// key. `version.file` persists only {key, name, contentType, size}; the short-lived presigned
// GET url is resolved on view-open via limsForskrifter.getReadUrl(file.key) (the SP-E download
// contract) and held in local state — never cached in redux, since it expires in ~1h. While it
// resolves (or if there is no key), the open/download actions stay disabled and the PDF preview
// is hidden. A definition with no linked-and-effective forskrift renders a soft "no forskrift"
// note — never a hard block (the analysis still runs).
export default function ForskriftViewer({ effective }) {
  const { t } = useT("common")

  return (
    <div className="LimsForskriftOnAnalysis LimsCard">
      {/* #4217: a card with a card title, like every block on the Result-entry tab (was a large
          bare heading between two rules — SPEC-022 K6 / RR3 #3). */}
      <h4 className="LimsCard-title">{t("limsForskriftOnAnalysis")}</h4>
      {effective ? <EffectiveBlock effective={effective} t={t} /> : <NoForskrift t={t} />}
    </div>
  )
}

function EffectiveBlock({ effective, t }) {
  const dispatch = useDispatch()
  const { document: doc, version } = effective
  const file = version.file || {}
  const fileKey = file.key || null
  // Store the resolved url alongside the key it belongs to, so `url` derives to null whenever a
  // new file is selected and its resolution is still in flight — no synchronous reset needed.
  const [resolved, setResolved] = useState({ key: null, url: null })
  const url = resolved.key === fileKey ? resolved.url : null
  const isPdf = (file.contentType || "").includes("pdf") || (file.name || "").toLowerCase().endsWith(".pdf")

  // Resolve the presigned GET url whenever the effective file changes. Guard against a stale
  // resolution overwriting a newer one (the user switches analyses while a url is in flight).
  useEffect(() => {
    if (!fileKey) return undefined
    let active = true
    dispatch(limsForskrifter.getReadUrl(fileKey))
      .then((u) => {
        if (active) setResolved({ key: fileKey, url: u || null })
      })
      .catch(() => {
        if (active) setResolved({ key: fileKey, url: null })
      })
    return () => {
      active = false
    }
  }, [dispatch, fileKey])

  return (
    <>
      <div className="LimsForskriftOnAnalysis-doc">
        <FiFileText className="LimsForskriftOnAnalysis-icon" />
        <span className="LimsForskriftOnAnalysis-name">{doc.name}</span>
        <span className="LimsForskriftOnAnalysis-version">
          {t("limsForskriftVersion")} {version.versionNumber} · {t("limsForskriftStatusEffective")}
        </span>
        <span className="LimsForskriftOnAnalysis-filename">{file.name}</span>
        <a
          className={"LimsForskriftOnAnalysis-action" + (url ? "" : " is-disabled")}
          href={url || undefined}
          target="_blank"
          rel="noreferrer"
          aria-disabled={url ? undefined : true}
        >
          <FiExternalLink /> {t("limsForskriftOpen")}
        </a>
        <a
          className={"LimsForskriftOnAnalysis-action" + (url ? "" : " is-disabled")}
          href={url || undefined}
          download={file.name || undefined}
          aria-disabled={url ? undefined : true}
        >
          <FiDownload /> {t("limsForskriftDownload")}
        </a>
      </div>
      {url && isPdf && (
        <iframe
          className="LimsForskriftOnAnalysis-preview"
          title={`${doc.name} v${version.versionNumber}`}
          src={url}
        />
      )}
    </>
  )
}

function NoForskrift({ t }) {
  return (
    <div className="LimsForskriftOnAnalysis-warning">
      <FiAlertTriangle />
      <span>{t("limsForskriftSupersededWarning")}</span>
    </div>
  )
}
